import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, MessageCircle, Mail, Phone, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { logger } from '../../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LeadsManagement() {
  const { getAuthHeader } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/quotations`, { headers: getAuthHeader(), withCredentials: true })
      .then(({ data }) => setQuotations(data))
      .catch((e) => logger.error('Failed to load leads:', e))
      .finally(() => setLoading(false));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const openWhatsApp = (phone, name) => {
    const cleanPhone = phone.replace(/\D/g, '');
    const message = encodeURIComponent(`Olá ${name}! Aqui é da Done Technology. Recebemos seu pedido de orçamento.`);
    window.open(`https://wa.me/55${cleanPhone}?text=${message}`, '_blank');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="animate-spin text-[#22D3EE]" size={40} /></div>;
  }

  return (
    <div className="space-y-6" data-testid="leads-management">
      <div>
        <h1 className="text-3xl font-bold text-[#F8FAFC] mb-2">Gestão de Leads</h1>
        <p className="text-[#94A3B8]">{quotations.length} solicitações de orçamento</p>
      </div>

      {quotations.length === 0 ? (
        <div className="bg-[#0d0f17] border border-white/5 rounded-xl p-12 text-center">
          <p className="text-[#94A3B8]">Nenhum lead ainda. As solicitações de orçamento aparecerão aqui.</p>
        </div>
      ) : (
        <div className="bg-[#0d0f17] border border-white/5 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-white/5 border-b border-white/5">
              <tr>
                <th className="text-left p-4 text-sm font-semibold text-[#94A3B8]">Nome</th>
                <th className="text-left p-4 text-sm font-semibold text-[#94A3B8] hidden md:table-cell">Email</th>
                <th className="text-left p-4 text-sm font-semibold text-[#94A3B8] hidden lg:table-cell">Tipo</th>
                <th className="text-left p-4 text-sm font-semibold text-[#94A3B8]">Status</th>
                <th className="text-left p-4 text-sm font-semibold text-[#94A3B8]">Ações</th>
              </tr>
            </thead>
            <tbody>
              {quotations.map((q) => (
                <React.Fragment key={q.id}>
                  <tr
                    data-testid={`lead-row-${q.id}`}
                    className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                    onClick={() => setExpanded(expanded === q.id ? null : q.id)}
                  >
                    <td className="p-4">
                      <p className="text-[#F8FAFC] font-medium">{q.name}</p>
                      {q.company && <p className="text-xs text-[#94A3B8]">{q.company}</p>}
                    </td>
                    <td className="p-4 text-sm text-[#94A3B8] hidden md:table-cell">{q.email}</td>
                    <td className="p-4 hidden lg:table-cell">
                      <span className="text-xs bg-[#22D3EE]/10 text-[#22D3EE] px-2 py-1 rounded">
                        {q.project_type}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-xs bg-white/5 text-[#F8FAFC] px-2 py-1 rounded">
                        {q.status}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); openWhatsApp(q.phone, q.name); }}
                          data-testid={`whatsapp-btn-${q.id}`}
                          className="p-2 rounded bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 transition-colors"
                        >
                          <MessageCircle size={14} />
                        </button>
                        {expanded === q.id ? <ChevronUp size={16} className="text-[#94A3B8]" /> : <ChevronDown size={16} className="text-[#94A3B8]" />}
                      </div>
                    </td>
                  </tr>
                  {expanded === q.id && (
                    <tr className="bg-white/[0.02]">
                      <td colSpan={5} className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-[#94A3B8] mb-1">Telefone</p>
                            <p className="text-[#F8FAFC]">{q.phone}</p>
                          </div>
                          <div>
                            <p className="text-[#94A3B8] mb-1">Orçamento</p>
                            <p className="text-[#F8FAFC]">{q.budget_range}</p>
                          </div>
                          <div>
                            <p className="text-[#94A3B8] mb-1">Prazo</p>
                            <p className="text-[#F8FAFC]">{q.deadline}</p>
                          </div>
                          <div>
                            <p className="text-[#94A3B8] mb-1">Funcionalidades</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {q.features?.map((f) => (
                                <span key={f} className="text-xs bg-[#4F46E5]/10 text-[#4F46E5] px-2 py-0.5 rounded">{f}</span>
                              ))}
                            </div>
                          </div>
                          {q.description && (
                            <div className="md:col-span-2">
                              <p className="text-[#94A3B8] mb-1">Descrição</p>
                              <p className="text-[#F8FAFC]">{q.description}</p>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
